// IncidentsContextValidators.js
import { object, string, mixed, number, array } from "yup";

/**
 * Primitives (permissive for now; tighten later to .uuid() / integer() etc.)
 */
const idValue = mixed()
  .nullable(true)
  .test(
    "id-type",
    "id/uuid must be string or number",
    v => v == null || typeof v === "string" || typeof v === "number"
  );
const uuidValue = string().nullable(true); // later: .uuid()

/**
 * Helpers
 */
const hasAny = (o, keys) =>
  keys.some(
    k =>
      Object.prototype.hasOwnProperty.call(o || {}, k) &&
      o[k] !== null &&
      o[k] !== ""
  );

/** Build a unique at-least-one test for a given key set */
const atLeastOne = (keys, label) => ({
  name: `at-least-one:${keys.join("|")}`,
  // IMPORTANT: use the 'value' param, not this.value
  test(value) {
    const ok = hasAny(value || {}, keys);
    return ok
      ? true
      : this.createError({ message: `Provide ${label || keys.join(" or ")}` });
  },
});

/** Forbid specific keys; lists any that appear (even if value is 0/false/empty string) */
const forbidKeys = keys => ({
  name: `forbid-keys:${keys.join("|")}`,
  test(value) {
    const v = value || {};
    const present = keys.filter(k =>
      Object.prototype.hasOwnProperty.call(v, k)
    );
    return present.length
      ? this.createError({
          message: `Forbidden keys present: ${present.join(", ")}`,
        })
      : true;
  },
});

/**
 * Standard result object
 */
const makeResult = ({ ok, type, payload, err }) =>
  ok
    ? { valid: true, error: null, details: [], actionType: type, payload }
    : {
        valid: false,
        error: `${type}: ${
          Array.isArray(err?.errors)
            ? err.errors.join(" | ")
            : String(err?.message || "Invalid payload")
        }`,
        details: Array.isArray(err?.inner)
          ? err.inner.map(e => ({ path: e.path, message: e.message }))
          : [],
        actionType: type,
        payload,
      };

const OPEN_INCIDENT_Payload = mixed()
  .required("Payload is required")
  .test(
    "open-incident:type",
    "Payload must be a string or number",
    v => typeof v === "string" || typeof v === "number"
  );

const CLOSE_INCIDENT_Payload = mixed()
  .required("Payload is required")
  .test(
    "close-incident:type",
    "Payload must be a string or number",
    v => typeof v === "string" || typeof v === "number"
  );

// set-initial-call-info: payload is an object whose first (only) key maps to an incident with id/uuid
const SET_INITIAL_CALL_INFO_Payload = mixed().test({
  name: "set-initial-call-info:shape",
  test(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return this.createError({
        message: "Payload must be an object containing a nested incident",
      });
    }
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return this.createError({
        message: "Payload object must contain at least one nested incident",
      });
    }
    const first = value[keys[0]];
    if (!first || typeof first !== "object") {
      return this.createError({ message: "Nested incident must be an object" });
    }
    if (!hasAny(first, ["id", "uuid", "incidentId", "incidentUuid"])) {
      return this.createError({
        message: "Nested incident must include id or uuid",
      });
    }
    return true;
  },
});

// set-nic-entry: requires incidentIndex (number) and value (object)
const SET_NIC_ENTRY_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .required("incidentIndex is required"),
  value: mixed()
    .defined("value is required")
    .test({
      name: "value:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "value must be an object" });
      },
    }),
})
  .typeError("Payload must be an object") // nicer than “this cannot be null”
  .required("Payload is required"); // undefined payload fails

const ADD_OFFLINE_INCIDENT_Payload = object({
  uuid: string().required("uuid is required"), // later: .uuid() if you standardize
  // Optional fields (kept permissive)
  id: mixed(),
  callTime: mixed().nullable(true),
  incidentNumber: mixed().nullable(true),
  personIncidentSpecifics: mixed(), // can tighten to array() later
  officerIncidentSpecifics: mixed(), // can tighten to array() later
  address: mixed(), // can tighten to object() later
})
  .typeError("Payload must be an object") // nicer than “this cannot be null”
  .required("Payload is required"); // undefined payload fails

// ---------- update-incident ----------
// make sure you have: import { object, string, mixed, number } from 'yup';

const UPDATE_INCIDENT_Payload = object({
  // addressing
  id: mixed(), // permissive (string or number)
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  // fields
  accessor: string(), // optional
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-incident",
    test(value) {
      const v = value || {};
      const hasAny = (obj, keys) =>
        keys.some(
          k =>
            Object.prototype.hasOwnProperty.call(obj, k) &&
            obj[k] !== null &&
            obj[k] !== ""
        );
      const hasId = hasAny(v, ["id"]);
      const hasIndexKey = Object.prototype.hasOwnProperty.call(
        v,
        "incidentIndex"
      ); // counts 0 as present
      return hasId || hasIndexKey
        ? true
        : this.createError({
            message: "Provide id (or incidentIndex)",
          });
    },
  });

// ---------- update-incident-address-alert ----------
const UPDATE_INCIDENT_ADDRESS_ALERT_Payload = object({
  addressId: mixed().required("addressId is required"),
  addressAlertId: mixed().required("addressAlertId is required"),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

// ---------- update-incident-detail ----------
// make sure you're importing: object, string, mixed, number
const UPDATE_INCIDENT_DETAIL_Payload = object({
  // addressing
  incidentId: mixed(), // permissive (string/number)
  incidentUuid: string(), // tighten later: .uuid()
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),

  // fields
  property: string(), // optional
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  // addressing: require id/uuid OR incidentIndex (0 allowed)
  .test({
    name: "addressing:update-incident-detail",
    test(value) {
      const v = value || {};
      const hasAny = (obj, keys) =>
        keys.some(
          k =>
            Object.prototype.hasOwnProperty.call(obj, k) &&
            obj[k] !== null &&
            obj[k] !== ""
        );
      const hasId = hasAny(v, ["incidentId", "incidentUuid"]);
      const hasIndexKey = Object.prototype.hasOwnProperty.call(
        v,
        "incidentIndex"
      ); // counts 0 as present
      return hasId || hasIndexKey
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid (or incidentIndex)",
          });
    },
  })
  // merge mode: if no property, value must be a plain object
  .test({
    name: "value:object-when-no-property",
    test(value) {
      const v = value || {};
      const hasProp = typeof v.property === "string" && v.property !== "";
      if (hasProp) return true; // update-by-property: any value allowed (incl. null/0/'')
      const val = v.value;
      const ok = val && typeof val === "object" && !Array.isArray(val);
      return ok
        ? true
        : this.createError({
            message: "value must be an object when property is not provided",
          });
    },
  });

/** remove-incident-detail-narrative
 * EITHER:
 *   - incidentIndex (number) AND narrativeIndex (number)
 * OR:
 *   - incidentId/incidentUuid AND narrativeId/uuid
 */
const REMOVE_INCIDENT_DETAIL_NARRATIVE_Payload = object({
  // index addressing (optional)
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  narrativeIndex: number()
    .typeError("narrativeIndex must be a number")
    .integer("narrativeIndex must be an integer")
    .min(0, "narrativeIndex must be >= 0"),

  // id/uuid addressing (optional; allow nulls so they don't fail type layer)
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  narrativeId: idValue.nullable(true),
  uuid: uuidValue.nullable(true), // narrative UUID (reducer expects `payload.uuid`)
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-incident-detail-narrative",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);

      // If BOTH indices are present, we're good (0 is valid; we only check presence).
      const usingIndices = has("incidentIndex") && has("narrativeIndex");
      if (usingIndices) return true;

      // Otherwise require id/uuid pairs via your "atLeastOne" semantics:
      if (!hasAny(v, ["incidentId", "incidentUuid"])) {
        return this.createError({
          message: "Provide incidentId or incidentUuid",
        });
      }
      if (!hasAny(v, ["narrativeId", "uuid"])) {
        return this.createError({ message: "Provide narrativeId or uuid" });
      }
      return true;
    },
  });

/** add-incident-detail-narrative
 * Requires:
 *   - narrative (object)
 * And addressing with EITHER incidentIndex OR incidentId/incidentUuid
 */
const ADD_INCIDENT_DETAIL_NARRATIVE_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentId: mixed(),
  incidentUuid: string(),

  narrative: mixed()
    .defined("narrative is required")
    .test({
      name: "narrative:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "narrative must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:add-incident-detail-narrative",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const idOK =
        (Object.prototype.hasOwnProperty.call(v, "incidentId") &&
          v.incidentId !== null &&
          v.incidentId !== "") ||
        (Object.prototype.hasOwnProperty.call(v, "incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      const idxOK = has("incidentIndex");
      return idOK || idxOK
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid (or incidentIndex)",
          });
    },
  });

/** update-incident-detail-narrative
 * Requires:
 *   - property (string)
 *   - value (defined; may be null/"")
 * Addressing EITHER:
 *   A) incidentIndex AND narrativeIndex
 *   B) incidentId/incidentUuid AND narrativeId/uuid
 */
const UPDATE_INCIDENT_DETAIL_NARRATIVE_Payload = object({
  // indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  narrativeIndex: number()
    .typeError("narrativeIndex must be a number")
    .integer("narrativeIndex must be an integer")
    .min(0, "narrativeIndex must be >= 0"),

  // ids
  incidentId: mixed(),
  incidentUuid: string(),
  narrativeId: mixed(), // reducer compares this to id OR uuid
  uuid: string(), // allow narrative uuid explicitly too

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-incident-detail-narrative",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const hasAny = keys =>
        keys.some(k => has(k) && v[k] !== null && v[k] !== "");
      const indicesOK = has("incidentIndex") && has("narrativeIndex");
      const idsOK =
        hasAny(["incidentId", "incidentUuid"]) &&
        hasAny(["narrativeId", "uuid"]);
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex and narrativeIndex, or incidentId/incidentUuid and narrativeId/uuid",
          });
    },
  });

// set-dispatch-narratives: incidentId required; segments must be an array (can be empty)
const SET_DISPATCH_NARRATIVES_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  segments: array()
    .defined("segments is required")
    .typeError("segments must be an array"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

// add-or-update-dispatch-narrative: incidentId + segment object
const ADD_OR_UPDATE_DISPATCH_NARRATIVE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  segment: mixed()
    .defined("segment is required")
    .test({
      name: "segment:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "segment must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

// remove-dispatch-narrative: incidentId + segmentId
const REMOVE_DISPATCH_NARRATIVE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  segmentId: mixed().required("segmentId is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** replace-incident
 * Requires at least one of: id or uuid.
 * incidentNumber is optional. No index addressing here.
 */
const REPLACE_INCIDENT_Payload = object({
  id: mixed(), // string/number ok
  uuid: string(), // can tighten to .uuid() later
  incidentNumber: mixed().nullable(true),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:replace-incident",
    test(value) {
      const v = value || {};
      const hasAny =
        (Object.prototype.hasOwnProperty.call(v, "id") &&
          v.id !== null &&
          v.id !== "") ||
        (Object.prototype.hasOwnProperty.call(v, "uuid") &&
          v.uuid !== null &&
          v.uuid !== "");
      return hasAny
        ? true
        : this.createError({ message: "Provide id or uuid" });
    },
  });

/** add-incident-to-link
 * Requires linkedIncident (object).
 * Address via incidentIndex OR incidentId/incidentUuid.
 */
const ADD_INCIDENT_TO_LINK_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentId: mixed(),
  incidentUuid: string(),
  linkedIncident: mixed()
    .defined("linkedIncident is required")
    .test({
      name: "linkedIncident:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "linkedIncident must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:add-incident-to-link",
    test(value) {
      const v = value || {};
      const hasIndex = Object.prototype.hasOwnProperty.call(v, "incidentIndex");
      const hasId =
        (Object.prototype.hasOwnProperty.call(v, "incidentId") &&
          v.incidentId !== null &&
          v.incidentId !== "") ||
        (Object.prototype.hasOwnProperty.call(v, "incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      return hasIndex || hasId
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid (or incidentIndex)",
          });
    },
  });

/** remove-incident-from-link
 * Current reducer uses only incidentId and linkedIncidentId.
 */
const REMOVE_INCIDENT_FROM_LINK_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  linkedIncidentId: mixed().required("linkedIncidentId is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** add-empty-ois
 * Requires:
 *   - ois (object)
 * And addressing with EITHER incidentIndex OR incidentId/incidentUuid
 */
const ADD_EMPTY_OIS_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentId: mixed(),
  incidentUuid: string(),
  ois: mixed()
    .defined("ois is required")
    .test({
      name: "ois:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "ois must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:add-empty-ois",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const idOK =
        (has("incidentId") && v.incidentId !== null && v.incidentId !== "") ||
        (has("incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      const idxOK = has("incidentIndex");
      return idOK || idxOK
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid (or incidentIndex)",
          });
    },
  });

/** update-ois-multi-field
 * Requires:
 *   - incidentId (string/number or uuid; reducer compares this to incident.id OR incident.uuid)
 *   - updatedOis (object)
 * oisId / oisUuid are optional; if omitted, reducer pushes updatedOis as new
 */
const UPDATE_OIS_MULTI_FIELD_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  oisId: mixed(),
  oisUuid: string(),
  updatedOis: mixed()
    .defined("updatedOis is required")
    .test({
      name: "updatedOis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "updatedOis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

// update-ois: incident (id/uuid) AND ois (id/uuid) + property/value
const UPDATE_OIS_Payload = object({
  incidentId: idValue,
  incidentUuid: uuidValue,
  oisId: idValue,
  oisUuid: uuidValue,
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .test(
    atLeastOne(["incidentId", "incidentUuid"], "incidentId or incidentUuid")
  )
  .test(atLeastOne(["oisId", "oisUuid"], "oisId or oisUuid"));
// .test(forbidKeys(["incidentIndex", "oisIndex"]));

/** update-ois-from-uuid
 * Requires:
 *   - incidentId
 *   - oisUuid
 *   - ois object with id (and optionally __typename)
 */
const UPDATE_OIS_FROM_UUID_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  oisUuid: string().required("oisUuid is required"),
  ois: object({
    id: mixed().required("ois.id is required"),
    __typename: mixed(),
  })
    .typeError("ois must be an object")
    .required("ois is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** merge-pis
 * Requires: incidentId OR incidentUuid, and value object with value.id
 */
const MERGE_PIS_Payload = object({
  incidentId: mixed(),
  incidentUuid: string(),
  value: object({
    id: mixed().required("value.id is required"),
  })
    .typeError("value must be an object")
    .required("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:merge-pis",
    test(value) {
      const v = value || {};
      const hasId =
        (Object.prototype.hasOwnProperty.call(v, "incidentId") &&
          v.incidentId !== null &&
          v.incidentId !== "") ||
        (Object.prototype.hasOwnProperty.call(v, "incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      return hasId
        ? true
        : this.createError({ message: "Provide incidentId or incidentUuid" });
    },
  });

// update-pis: incident (id/uuid) AND pis (id/uuid) + property/value
const UPDATE_PIS_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  pisId: idValue.nullable(true),
  pisUuid: uuidValue.nullable(true),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .test(
    atLeastOne(["incidentId", "incidentUuid"], "incidentId or incidentUuid")
  )
  .test(atLeastOne(["pisId", "pisUuid"], "pisId or pisUuid"))
  .test(forbidKeys(["incidentIndex", "pisIndex"]));

/** add-vis
 * Requires: vis object; address via incidentIndex OR incidentId/incidentUuid
 * (vis.id is optional; reducer will push if not found)
 */
const ADD_VIS_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentId: mixed(),
  incidentUuid: string(),
  vis: mixed()
    .defined("vis is required")
    .test({
      name: "vis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "vis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:add-vis",
    test(value) {
      const v = value || {};
      const hasIdx = Object.prototype.hasOwnProperty.call(v, "incidentIndex");
      const hasId =
        (Object.prototype.hasOwnProperty.call(v, "incidentId") &&
          v.incidentId !== null &&
          v.incidentId !== "") ||
        (Object.prototype.hasOwnProperty.call(v, "incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      return hasIdx || hasId
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid (or incidentIndex)",
          });
    },
  });

// update-vis: incident (id/uuid) AND vis (id/uuid) + property/value
const UPDATE_VIS_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  visId: idValue.nullable(true),
  visUuid: uuidValue.nullable(true),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(
    atLeastOne(["incidentId", "incidentUuid"], "incidentId or incidentUuid")
  )
  .test(atLeastOne(["visId", "visUuid"], "visId or visUuid"))
  .test(forbidKeys(["incidentIndex", "visIndex"]));

/** update-vehicle
 * Requires: property + value; address EITHER
 *   A) incidentIndex AND visIndex, OR
 *   B) vehicleId OR vehicleUuid
 */
const UPDATE_VEHICLE_Payload = object({
  // A) index addressing
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  visIndex: number()
    .typeError("visIndex must be a number")
    .integer("visIndex must be an integer")
    .min(0, "visIndex must be >= 0"),

  // B) vehicle addressing
  vehicleId: mixed().nullable(true),
  vehicleUuid: mixed().nullable(true), // reducer matches against vis.vehicle.id/uuid

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-vehicle",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const indicesOK = has("incidentIndex") && has("visIndex");
      const vehicleOK =
        (has("vehicleId") && v.vehicleId !== null && v.vehicleId !== "") ||
        (has("vehicleUuid") && v.vehicleUuid !== null && v.vehicleUuid !== "");
      return indicesOK || vehicleOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex and visIndex, or vehicleId/vehicleUuid",
          });
    },
  });

const EXTEND_PIS_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  pisId: idValue.nullable(true),
  pisUuid: uuidValue.nullable(true),
  pis: mixed()
    .defined("pis is required")
    .test({
      name: "pis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "pis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(forbidKeys(["incidentIndex", "pisIndex"]))
  .test({
    name: "addressing:extend-pis",
    test(value) {
      const v = value || {};
      const hasIncident = hasAny(v, ["incidentId", "incidentUuid"]);
      const hasPis = hasAny(v, ["pisId", "pisUuid"]);
      if (hasIncident && hasPis) return true;

      return this.createError({
        message:
          "Provide incidentId/incidentUuid and pisId/pisUuid",
      });
    },
  });

/** update-pis-from-uuid
 * Requires: incidentId, pisUuid, and pis { id }
 * (Reducer only checks incidentId today.)
 */
const UPDATE_PIS_FROM_UUID_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  pisUuid: string().required("pisUuid is required"),
  pis: object({
    id: mixed().required("pis.id is required"),
    bac: mixed(),
    __typename: mixed(),
  })
    .typeError("pis must be an object")
    .required("pis is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** add-pis
 * Requires: incidentId and pis object
 * (Reducer only checks incidentId today.)
 */
const ADD_PIS_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  pis: mixed()
    .defined("pis is required")
    .test({
      name: "pis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "pis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

const ADD_OR_UPDATE_PIS_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  pisId: idValue.nullable(true),
  pisUuid: uuidValue.nullable(true),
  pis: mixed()
    .defined("pis is required")
    .test({
      name: "pis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "pis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(forbidKeys(["incidentIndex", "pisIndex"]))
  .test({
    name: "addressing:add-or-update-pis",
    test(value) {
      const v = value || {};
      return hasAny(v, ["incidentId", "incidentUuid"])
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid",
          });
    },
  });

/** add-or-update-vis
 * Requires: vis (object)
 * Address via EITHER incidentIndex OR incidentId/incidentUuid
 */
const ADD_OR_UPDATE_VIS_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentId: mixed(),
  incidentUuid: string(),

  vis: mixed()
    .defined("vis is required")
    .test({
      name: "vis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "vis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:add-or-update-vis",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const hasId =
        (has("incidentId") && v.incidentId !== null && v.incidentId !== "") ||
        (has("incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      const hasIdx = has("incidentIndex");
      return hasId || hasIdx
        ? true
        : this.createError({
            message: "Provide incidentId or incidentUuid (or incidentIndex)",
          });
    },
  });

/** update-person-in-pis
 * Requires: incidentId/incidentUuid AND pisId/pisUuid
 * Also requires: property (string) and value (defined; can be null/0/"")
 * Optional: accessor (string)
 */
const UPDATE_PERSON_IN_PIS_Payload = object({
  incidentId: mixed(),
  incidentUuid: string().nullable(true),

  pisId: mixed(),
  pisUuid: string().nullable(true),

  accessor: string(),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-person-in-pis:incident",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const ok =
        (has("incidentId") && v.incidentId !== null && v.incidentId !== "") ||
        (has("incidentUuid") &&
          v.incidentUuid !== null &&
          v.incidentUuid !== "");
      return ok
        ? true
        : this.createError({ message: "Provide incidentId or incidentUuid" });
    },
  })
  .test({
    name: "addressing:update-person-in-pis:pis",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const ok =
        (has("pisId") && v.pisId !== null && v.pisId !== "") ||
        (has("pisUuid") && v.pisUuid !== null && v.pisUuid !== "");
      return ok
        ? true
        : this.createError({ message: "Provide pisId or pisUuid" });
    },
  });

const UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS_Payload = object({
  personId: idValue.nullable(true),
  personUuid: uuidValue.nullable(true),

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(forbidKeys(["incidentIndex", "pisIndex"]))
  .test({
    name: "addressing:update-person-personal-attribute-in-pis",
    test(value) {
      const v = value || {};
      const atLeastOnePerson = atLeastOne(
        ["personId", "personUuid"],
        "personId or personUuid"
      );
      return atLeastOnePerson.test.call(this, v);
    },
  });

const UPDATE_PERSON_ATTRIBUTE_Payload = object({
  personId: idValue.nullable(true),
  personUuid: uuidValue.nullable(true),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(forbidKeys(["incidentIndex", "personIndex", "pisIndex"]))
  .test(atLeastOne(["personId", "personUuid"], "personId or personUuid"));

const MERGE_PERSON_PERSONAL_ATTRIBUTE_Payload = object({
  personId: idValue.nullable(true),
  personUuid: uuidValue.nullable(true),
  value: mixed()
    .defined("value is required")
    .test({
      name: "value:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "value must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(forbidKeys(["incidentIndex", "personIndex", "pisIndex"]))
  .test(atLeastOne(["personId", "personUuid"], "personId or personUuid"));

const UPDATE_PERSON_PERSONAL_ATTRIBUTE_Payload = object({
  personId: idValue.nullable(true),
  personUuid: uuidValue.nullable(true),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(forbidKeys(["incidentIndex", "personIndex", "pisIndex"]))
  .test(atLeastOne(["personId", "personUuid"], "personId or personUuid"));

/** remove-pis
 * Requires: incidentId and pisId (reducer only checks id, not uuid)
 */
const REMOVE_PIS_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  pisId: mixed().required("pisId is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-ois
 * Requires: incidentId AND (oisId OR uuid)
 * (Reducer reads `uuid` for ois uuid.)
 */
const REMOVE_OIS_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  oisId: mixed(),
  uuid: mixed(),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-ois",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const ok =
        (has("oisId") && v.oisId !== null && v.oisId !== "") ||
        (has("uuid") && v.uuid !== null && v.uuid !== "");
      return ok ? true : this.createError({ message: "Provide oisId or uuid" });
    },
  });

/** remove-vis
 * Address EITHER:
 *   A) incidentIndex AND visIndex
 *   B) incidentId AND (visId OR uuid)
 */
const REMOVE_VIS_Payload = object({
  // A) indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  visIndex: number()
    .typeError("visIndex must be a number")
    .integer("visIndex must be an integer")
    .min(0, "visIndex must be >= 0"),

  // B) ids
  incidentId: mixed(),
  visId: mixed(),
  uuid: mixed(), // VIS uuid (payload key is `uuid` in reducer)
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-vis",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const indicesOK = has("incidentIndex") && has("visIndex");
      const idsOK =
        present("incidentId") && (present("visId") || present("uuid"));
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex and visIndex, or incidentId and (visId or uuid)",
          });
    },
  });

/** add-offense
 * Requires: incidentId and offense object
 */
const ADD_OFFENSE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  offense: mixed()
    .defined("offense is required")
    .test({
      name: "offense:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "offense must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-offense
 * Requires: incidentId AND (offenseId OR uuid)
 */
const REMOVE_OFFENSE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  offenseId: mixed(),
  uuid: mixed(),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-offense",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      return present("offenseId") || present("uuid")
        ? true
        : this.createError({ message: "Provide offenseId or uuid" });
    },
  });

/** update-offense
 * Requires: incidentId AND (offenseId OR uuid) AND property/value
 */
const UPDATE_OFFENSE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  offenseId: mixed(),
  uuid: mixed(),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-offense",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      return present("offenseId") || present("uuid")
        ? true
        : this.createError({ message: "Provide offenseId or uuid" });
    },
  });

/** update-sva
 * Requires: incidentId, offenseId, svaId, property, value
 * (Reducer uses payload.svaId for either SVA id or uuid)
 */
const UPDATE_SVA_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  offenseId: mixed().required("offenseId is required"),
  svaId: mixed().required("svaId is required"),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** update-sva-all
 * Iterate over ALL incidents and update the matching SVA in the matching offense.
 * Requires: (offenseId OR offenseUuid) AND (svaId OR svaUuid) + property + value
 * Forbids: incidentId/incidentUuid (and incidentIndex)
 */
const UPDATE_SVA_ALL_INCIDENTS_Payload = object({
  offenseId: idValue.nullable(true),
  offenseUuid: uuidValue.nullable(true),
  svaId: idValue.nullable(true),
  svaUuid: uuidValue.nullable(true),

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(atLeastOne(["offenseId", "offenseUuid"], "offenseId or offenseUuid"))
  .test(atLeastOne(["svaId", "svaUuid"], "svaId or svaUuid"))
  .test(forbidKeys(["incidentId", "incidentUuid", "incidentIndex"]));

/** add-sva
 * Requires: incidentId, offenseId, and sva object
 */
const ADD_SVA_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  offenseId: mixed().required("offenseId is required"),
  sva: mixed()
    .defined("sva is required")
    .test({
      name: "sva:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "sva must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-sva
 * Requires: incidentId, offenseId, and (svaId or svaUuid)
 */
const REMOVE_SVA_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  offenseId: mixed().required("offenseId is required"),
  svaId: mixed(),
  svaUuid: mixed(),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-sva",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      return present("svaId") || present("svaUuid")
        ? true
        : this.createError({ message: "Provide svaId or svaUuid" });
    },
  });

/** add-arrest
 * Requires: incidentId and arrest object
 */
const ADD_ARREST_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  arrest: mixed()
    .defined("arrest is required")
    .test({
      name: "arrest:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "arrest must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-arrest
 * Requires: (arrestId or arrestUuid)
 * (incidentId is not required by the reducer)
 */
const REMOVE_ARREST_Payload = object({
  incidentId: mixed(), // optional
  arrestId: mixed(),
  arrestUuid: mixed(),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-arrest",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      return present("arrestId") || present("arrestUuid")
        ? true
        : this.createError({ message: "Provide arrestId or arrestUuid" });
    },
  });

/** update-arrest
 * Requires: arrestId (can be the UUID string), property, value
 * (Reducer matches action.payload.arrestId against arrest.id or arrest.uuid)
 */
const UPDATE_ARREST_Payload = object({
  arrestId: mixed().required("arrestId is required"),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** add-charge
 * Requires: incidentId, arrestId, and charge object
 */
const ADD_CHARGE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  arrestId: mixed().required("arrestId is required"),
  charge: mixed()
    .defined("charge is required")
    .test({
      name: "charge:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "charge must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-charge
 * Requires: incidentId, arrestId, and (chargeId OR chargeUuid)
 */
const REMOVE_CHARGE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  arrestId: mixed().required("arrestId is required"),
  chargeId: mixed(),
  chargeUuid: mixed(),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-charge",
    test(value) {
      const v = value || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      return present("chargeId") || present("chargeUuid")
        ? true
        : this.createError({ message: "Provide chargeId or chargeUuid" });
    },
  });

/** update-charge
 * Requires: incidentId, arrestId, chargeId, property, value
 * (chargeId can be either DB id or the uuid string; reducer matches against both)
 */
const UPDATE_CHARGE_Payload = object({
  incidentId: mixed().required("incidentId is required"),
  arrestId: mixed().required("arrestId is required"),
  chargeId: mixed().required("chargeId is required"),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** update-charge-all-incidents
 * Iterate over ALL incidents: update a matching charge inside a matching arrest.
 * Requires: (arrestId OR arrestUuid) AND (chargeId OR chargeUuid) + property + value
 * Forbids: incident-level addressing keys
 */
const UPDATE_CHARGE_ALL_INCIDENTS_Payload = object({
  arrestId: idValue.nullable(true),
  arrestUuid: uuidValue.nullable(true),
  chargeId: idValue.nullable(true),
  chargeUuid: uuidValue.nullable(true),

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(atLeastOne(["arrestId", "arrestUuid"], "arrestId or arrestUuid"))
  .test(atLeastOne(["chargeId", "chargeUuid"], "chargeId or chargeUuid"))
  .test(forbidKeys(["incidentId", "incidentUuid", "incidentIndex"]));

/** add-property
 * Reducer only supports incidentIndex + incidentProperty (object) today.
 */
const ADD_PROPERTY_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .required("incidentIndex is required"),
  incidentProperty: mixed()
    .defined("incidentProperty is required")
    .test({
      name: "incidentProperty:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "incidentProperty must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-property
 * Reducer only supports indices: incidentIndex + incidentPropertyIndex
 */
const REMOVE_PROPERTY_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .required("incidentIndex is required"),
  incidentPropertyIndex: number()
    .typeError("incidentPropertyIndex must be a number")
    .integer("incidentPropertyIndex must be an integer")
    .min(0, "incidentPropertyIndex must be >= 0")
    .required("incidentPropertyIndex is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** update-property
 * Two addressing modes:
 *  A) Indices: incidentIndex + propertyIndex
 *     - If property provided: value (any, defined) updates that key.
 *     - If property omitted: value must be an object (merge mode).
 *  B) IDs: incidentId/incidentUuid + incidentPropertyId
 *     - property (required) + value (defined)
 */
const UPDATE_PROPERTY_Payload = object({
  // A) indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  propertyIndex: number()
    .typeError("propertyIndex must be a number")
    .integer("propertyIndex must be an integer")
    .min(0, "propertyIndex must be >= 0"),

  // B) id addressing
  incidentId: mixed(),
  incidentUuid: string(),
  incidentPropertyId: mixed(),

  // fields
  property: string(), // optional in merge mode
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  // addressing
  .test({
    name: "addressing:update-property",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const indicesOK = has("incidentIndex") && has("propertyIndex");
      const idsOK =
        (present("incidentId") || present("incidentUuid")) &&
        present("incidentPropertyId");
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex and propertyIndex, or incidentId/incidentUuid and incidentPropertyId",
          });
    },
  })
  // merge mode rule when using indices
  .test({
    name: "merge-mode:update-property",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const indicesOK = has("incidentIndex") && has("propertyIndex");
      const hasProp = typeof v.property === "string" && v.property !== "";
      if (!indicesOK) return true; // only applies to index mode
      if (hasProp) return true; // normal update-by-key
      // merge mode: property omitted -> value must be plain object
      const val = v.value;
      const ok = val && typeof val === "object" && !Array.isArray(val);
      return ok
        ? true
        : this.createError({
            message: "value must be an object when property is not provided",
          });
    },
  })
  // id mode requires property (reducer path requires a key to set)
  .test({
    name: "id-mode:property-required",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const idsOK =
        (present("incidentId") || present("incidentUuid")) &&
        present("incidentPropertyId");
      if (!idsOK) return true;
      return typeof v.property === "string" && v.property !== ""
        ? true
        : this.createError({
            message: "property is required when using id addressing",
          });
    },
  });

/** add-custody-chain
 * Indices only: incidentIndex + incidentPropertyIndex + custodyChain(object)
 */
const ADD_CUSTODY_CHAIN_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .required("incidentIndex is required"),
  incidentPropertyIndex: number()
    .typeError("incidentPropertyIndex must be a number")
    .integer("incidentPropertyIndex must be an integer")
    .min(0, "incidentPropertyIndex must be >= 0")
    .required("incidentPropertyIndex is required"),
  custodyChain: mixed()
    .defined("custodyChain is required")
    .test({
      name: "custodyChain:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "custodyChain must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-custody-chain
 * Indices only: incidentIndex + incidentPropertyIndex + custodyChainIndex
 */
const REMOVE_CUSTODY_CHAIN_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .required("incidentIndex is required"),
  incidentPropertyIndex: number()
    .typeError("incidentPropertyIndex must be a number")
    .integer("incidentPropertyIndex must be an integer")
    .min(0, "incidentPropertyIndex must be >= 0")
    .required("incidentPropertyIndex is required"),
  custodyChainIndex: number()
    .typeError("custodyChainIndex must be a number")
    .integer("custodyChainIndex must be an integer")
    .min(0, "custodyChainIndex must be >= 0")
    .required("custodyChainIndex is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** update-custody-chain
 * Address EITHER:
 *  A) Indices: incidentIndex + incidentPropertyIndex + custodyChainIndex + property + value
 *  B) IDs: incidentPropertyId + custodyChainId + property + value
 */
const UPDATE_CUSTODY_CHAIN_Payload = object({
  // indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentPropertyIndex: number()
    .typeError("incidentPropertyIndex must be a number")
    .integer("incidentPropertyIndex must be an integer")
    .min(0, "incidentPropertyIndex must be >= 0"),
  custodyChainIndex: number()
    .typeError("custodyChainIndex must be a number")
    .integer("custodyChainIndex must be an integer")
    .min(0, "custodyChainIndex must be >= 0"),

  // ids
  incidentPropertyId: mixed(),
  custodyChainId: mixed(),

  // fields
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-custody-chain",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const indicesOK =
        has("incidentIndex") &&
        has("incidentPropertyIndex") &&
        has("custodyChainIndex");
      const idsOK = present("incidentPropertyId") && present("custodyChainId");
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex, incidentPropertyIndex, and custodyChainIndex, or incidentPropertyId and custodyChainId",
          });
    },
  });

/** add-lab-analysis
 * Indices only: incidentIndex + incidentPropertyIndex + labAnalysis(object)
 */
const ADD_LAB_ANALYSIS_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .required("incidentIndex is required"),
  incidentPropertyIndex: number()
    .typeError("incidentPropertyIndex must be a number")
    .integer("incidentPropertyIndex must be an integer")
    .min(0, "incidentPropertyIndex must be >= 0")
    .required("incidentPropertyIndex is required"),
  labAnalysis: mixed()
    .defined("labAnalysis is required")
    .test({
      name: "labAnalysis:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "labAnalysis must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** remove-lab-analysis
 * Indices only: incidentIndex + incidentPropertyIndex + labAnalysisIndex
 */
const REMOVE_LAB_ANALYSIS_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .required("incidentIndex is required"),
  incidentPropertyIndex: number()
    .typeError("incidentPropertyIndex must be a number")
    .integer("incidentPropertyIndex must be an integer")
    .min(0, "incidentPropertyIndex must be >= 0")
    .required("incidentPropertyIndex is required"),
  labAnalysisIndex: number()
    .typeError("labAnalysisIndex must be a number")
    .integer("labAnalysisIndex must be an integer")
    .min(0, "labAnalysisIndex must be >= 0")
    .required("labAnalysisIndex is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required");

/** update-lab-analysis
 * Address EITHER:
 *  A) Indices: incidentIndex + propertyIndex + labAnalysisIndex + property + value
 *  B) IDs: incidentPropertyId + labAnalysisId + property + value
 */
const UPDATE_LAB_ANALYSIS_Payload = object({
  // A) indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  propertyIndex: number()
    .typeError("propertyIndex must be a number")
    .integer("propertyIndex must be an integer")
    .min(0, "propertyIndex must be >= 0"),
  labAnalysisIndex: number()
    .typeError("labAnalysisIndex must be a number")
    .integer("labAnalysisIndex must be an integer")
    .min(0, "labAnalysisIndex must be >= 0"),

  // B) ids
  incidentPropertyId: mixed(),
  labAnalysisId: mixed(),

  // fields
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-lab-analysis",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const indicesOK =
        has("incidentIndex") && has("propertyIndex") && has("labAnalysisIndex");
      const idsOK = present("incidentPropertyId") && present("labAnalysisId");
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex, propertyIndex, and labAnalysisIndex, or incidentPropertyId and labAnalysisId",
          });
    },
  });

/** add-ticket-to-incident
 * Requires: ticket (object)
 * Address via incidentId or incidentUuid.
 * (Reducer only checks incidentId, not incidentUuid; we accept incidentUuid for future-compat.)
 */
const ADD_TICKET_TO_INCIDENT_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  ticket: mixed()
    .defined("ticket is required")
    .test({
      name: "ticket:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "ticket must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(
    atLeastOne(["incidentId", "incidentUuid"], "incidentId or incidentUuid")
  )
  .test(forbidKeys(["incidentIndex"]));

/** remove-incident-ticket
 * Address via incidentId/incidentUuid AND ticketId/ticketUuid.
 */
const REMOVE_INCIDENT_TICKET_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true),
  ticketId: idValue.nullable(true),
  ticketUuid: uuidValue.nullable(true),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(
    atLeastOne(["incidentId", "incidentUuid"], "incidentId or incidentUuid")
  )
  .test(atLeastOne(["ticketId", "ticketUuid"], "ticketId or ticketUuid"))
  .test(forbidKeys(["incidentIndex", "ticketIndex"]));

/** update-incident-ticket
 * Requires incidentId/incidentUuid AND ticketId/ticketUuid + property + value (+ optional accessor)
 */
const UPDATE_INCIDENT_TICKET_Payload = object({
  incidentId: idValue.nullable(true),
  incidentUuid: uuidValue.nullable(true), // reducer compares incidentId to incident.id or incident.uuid
  ticketId: idValue.nullable(true),
  ticketUuid: uuidValue.nullable(true),

  accessor: string(),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(
    atLeastOne(["incidentId", "incidentUuid"], "incidentId or incidentUuid")
  )
  .test(atLeastOne(["ticketId", "ticketUuid"], "ticketId or ticketUuid"))
  .test(forbidKeys(["incidentIndex", "ticketIndex"]));

/** update-ticket
 * Requires ticketId or ticketUuid + property + value (+ optional accessor)
 */
const UPDATE_TICKET_Payload = object({
  ticketId: idValue.nullable(true),
  ticketUuid: uuidValue.nullable(true),
  accessor: string(),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test(atLeastOne(["ticketId", "ticketUuid"], "ticketId or ticketUuid"))
  .test(forbidKeys(["ticketIndex"]));
/** update-incident-review
 * Address EITHER:
 *  A) incidentIndex + property + value
 *  B) (incidentId OR incidentUuid) + property + value
 */
const UPDATE_INCIDENT_REVIEW_Payload = object({
  // A) index mode
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),

  // B) id mode
  incidentId: mixed(),
  incidentUuid: mixed(),

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-incident-review",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const idxOK = has("incidentIndex");
      const idOK = present("incidentId") || present("incidentUuid");
      return idxOK || idOK
        ? true
        : this.createError({
            message: "Provide incidentId/incidentUuid or incidentIndex",
          });
    },
  });

/** add-nic
 * Requires: nicEntry object
 * Address via EITHER incidentIndex OR (incidentId/incidentUuid)
 */
const ADD_NIC_Payload = object({
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  incidentId: mixed(),
  incidentUuid: mixed(),
  nicEntry: mixed()
    .defined("nicEntry is required")
    .test({
      name: "nicEntry:is-object",
      test(v) {
        return v && typeof v === "object" && !Array.isArray(v)
          ? true
          : this.createError({ message: "nicEntry must be an object" });
      },
    }),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:add-nic",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const idxOK = has("incidentIndex");
      const idOK = present("incidentId") || present("incidentUuid");
      return idxOK || idOK
        ? true
        : this.createError({
            message: "Provide incidentId/incidentUuid or incidentIndex",
          });
    },
  });

/** remove-nic
 * Address EITHER:
 *  A) indices: incidentIndex + nicEntryIndex
 *  B) ids: (incidentId/incidentUuid) + (nicEntryId or nicEntryUuid)
 */
const REMOVE_NIC_Payload = object({
  // A) indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  nicEntryIndex: number()
    .typeError("nicEntryIndex must be a number")
    .integer("nicEntryIndex must be an integer")
    .min(0, "nicEntryIndex must be >= 0"),

  // B) ids
  incidentId: mixed(),
  incidentUuid: mixed(),
  nicEntryId: mixed(),
  nicEntryUuid: mixed(),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:remove-nic",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const indicesOK = has("incidentIndex") && has("nicEntryIndex");
      const idsOK =
        (present("incidentId") || present("incidentUuid")) &&
        (present("nicEntryId") || present("nicEntryUuid"));
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex and nicEntryIndex, or incidentId/incidentUuid and nicEntryId/nicEntryUuid",
          });
    },
  });

/** update-nic
 * Address EITHER:
 *  A) indices: incidentIndex + nicEntryIndex + property + value
 *  B) ids: (nicEntryId OR nicEntryUuid) + property + value
 * (incidentId is not required by reducer in id mode)
 */
const UPDATE_NIC_Payload = object({
  // A) indices
  incidentIndex: number()
    .typeError("incidentIndex must be a number")
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0"),
  nicEntryIndex: number()
    .typeError("nicEntryIndex must be a number")
    .integer("nicEntryIndex must be an integer")
    .min(0, "nicEntryIndex must be >= 0"),

  // B) ids
  nicEntryId: mixed(),
  nicEntryUuid: mixed(),
  // optional pass-throughs for future (not enforced)
  incidentId: mixed(),
  incidentUuid: mixed(),

  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-nic",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      const indicesOK = has("incidentIndex") && has("nicEntryIndex");
      const idsOK = present("nicEntryId") || present("nicEntryUuid");
      return indicesOK || idsOK
        ? true
        : this.createError({
            message:
              "Provide incidentIndex and nicEntryIndex, or nicEntryId/nicEntryUuid",
          });
    },
  });

/** update-query-returns
 * Requires: (incidentId OR incidentUuid) AND queryReturns (defined; may be null)
 */
const UPDATE_QUERY_RETURNS_Payload = object({
  incidentId: mixed(),
  incidentUuid: mixed(),
  queryReturns: mixed().nullable(true).defined("queryReturns is required"),
})
  .typeError("Payload must be an object")
  .required("Payload is required")
  .test({
    name: "addressing:update-query-returns",
    test(vRaw) {
      const v = vRaw || {};
      const has = k => Object.prototype.hasOwnProperty.call(v, k);
      const present = k => has(k) && v[k] !== null && v[k] !== "";
      return present("incidentId") || present("incidentUuid")
        ? true
        : this.createError({ message: "Provide incidentId or incidentUuid" });
    },
  });

const payloadSchemas = {
  "open-incident": OPEN_INCIDENT_Payload,
  "close-incident": CLOSE_INCIDENT_Payload,
  "set-initial-call-info": SET_INITIAL_CALL_INFO_Payload,
  "set-nic-entry": SET_NIC_ENTRY_Payload,
  "add-offline-incident": ADD_OFFLINE_INCIDENT_Payload,
  "update-incident": UPDATE_INCIDENT_Payload,
  "update-incident-address-alert": UPDATE_INCIDENT_ADDRESS_ALERT_Payload,
  "update-incident-detail": UPDATE_INCIDENT_DETAIL_Payload,
  "remove-incident-detail-narrative": REMOVE_INCIDENT_DETAIL_NARRATIVE_Payload,
  "add-incident-detail-narrative": ADD_INCIDENT_DETAIL_NARRATIVE_Payload,
  "update-incident-detail-narrative": UPDATE_INCIDENT_DETAIL_NARRATIVE_Payload,
  "set-dispatch-narratives": SET_DISPATCH_NARRATIVES_Payload,
  "add-or-update-dispatch-narrative": ADD_OR_UPDATE_DISPATCH_NARRATIVE_Payload,
  "remove-dispatch-narrative": REMOVE_DISPATCH_NARRATIVE_Payload,
  "replace-incident": REPLACE_INCIDENT_Payload,
  "add-incident-to-link": ADD_INCIDENT_TO_LINK_Payload,
  "remove-incident-from-link": REMOVE_INCIDENT_FROM_LINK_Payload,
  "add-empty-ois": ADD_EMPTY_OIS_Payload,
  "update-ois-multi-field": UPDATE_OIS_MULTI_FIELD_Payload,
  "update-ois": UPDATE_OIS_Payload,
  "update-ois-from-uuid": UPDATE_OIS_FROM_UUID_Payload,
  "merge-pis": MERGE_PIS_Payload,
  "update-pis": UPDATE_PIS_Payload,
  "add-vis": ADD_VIS_Payload,
  "update-vis": UPDATE_VIS_Payload,
  "update-vehicle": UPDATE_VEHICLE_Payload,
  "extend-pis": EXTEND_PIS_Payload,
  "update-pis-from-uuid": UPDATE_PIS_FROM_UUID_Payload,
  "add-pis": ADD_PIS_Payload,
  "add-or-update-pis": ADD_OR_UPDATE_PIS_Payload,
  "add-or-update-vis": ADD_OR_UPDATE_VIS_Payload,
  "update-person-in-pis": UPDATE_PERSON_IN_PIS_Payload,
  "update-person-personal-attribute-in-pis":
    UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS_Payload,
  "remove-pis": REMOVE_PIS_Payload,
  "remove-ois": REMOVE_OIS_Payload,
  "remove-vis": REMOVE_VIS_Payload,
  "add-offense": ADD_OFFENSE_Payload,
  "remove-offense": REMOVE_OFFENSE_Payload,
  "update-offense": UPDATE_OFFENSE_Payload,
  "update-sva": UPDATE_SVA_Payload,
  "update-sva-all-incidents": UPDATE_SVA_ALL_INCIDENTS_Payload,
  "add-sva": ADD_SVA_Payload,
  "remove-sva": REMOVE_SVA_Payload,
  "add-arrest": ADD_ARREST_Payload,
  "remove-arrest": REMOVE_ARREST_Payload,
  "update-arrest": UPDATE_ARREST_Payload,
  "add-charge": ADD_CHARGE_Payload,
  "remove-charge": REMOVE_CHARGE_Payload,
  "update-charge": UPDATE_CHARGE_Payload,
  "update-charge-all-incidents": UPDATE_CHARGE_ALL_INCIDENTS_Payload,
  "add-property": ADD_PROPERTY_Payload,
  "remove-property": REMOVE_PROPERTY_Payload,
  "update-property": UPDATE_PROPERTY_Payload,
  "add-custody-chain": ADD_CUSTODY_CHAIN_Payload,
  "remove-custody-chain": REMOVE_CUSTODY_CHAIN_Payload,
  "update-custody-chain": UPDATE_CUSTODY_CHAIN_Payload,
  "add-lab-analysis": ADD_LAB_ANALYSIS_Payload,
  "remove-lab-analysis": REMOVE_LAB_ANALYSIS_Payload,
  "update-lab-analysis": UPDATE_LAB_ANALYSIS_Payload,
  "add-ticket-to-incident": ADD_TICKET_TO_INCIDENT_Payload,
  "remove-incident-ticket": REMOVE_INCIDENT_TICKET_Payload,
  "update-incident-ticket": UPDATE_INCIDENT_TICKET_Payload,
  "update-ticket": UPDATE_TICKET_Payload,
  "update-person-attribute": UPDATE_PERSON_ATTRIBUTE_Payload,
  "merge-person-personal-attribute": MERGE_PERSON_PERSONAL_ATTRIBUTE_Payload,
  "update-person-personal-attribute": UPDATE_PERSON_PERSONAL_ATTRIBUTE_Payload,
  "update-incident-review": UPDATE_INCIDENT_REVIEW_Payload,
  "add-nic": ADD_NIC_Payload,
  "remove-nic": REMOVE_NIC_Payload,
  "update-nic": UPDATE_NIC_Payload,
  "update-query-returns": UPDATE_QUERY_RETURNS_Payload,
};

export function incidentReducerValidator(type, payload) {
  const schema = payloadSchemas[type];
  if (!schema)
    return {
      valid: false,
      error: "Case not found. Please update the validation.",
      details: [],
      actionType: type,
      payload,
    };

  try {
    // strict: true ensures no casting weirdness; keep unknowns allowed (we're permissive)
    schema.validateSync(payload, {
      abortEarly: false,
      stripUnknown: false,
      strict: true,
    });
    return makeResult({ ok: true, type, payload });
  } catch (err) {
    return makeResult({ ok: false, type, payload, err });
  }
}
